import { AppLayout } from '@/components/layouts/AppLayout'
import { PdfViewer } from '@/components/organisms/PdfViewer'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Skeleton } from '@/components/ui/skeleton'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { useFileDownloadUrl } from '@/hooks/use-file-download-url'
import { useDocumentTitle } from '@/hooks/useDocumentTitle'
import { fetchInvoiceById } from '@/services/invoices'
import { useQuery } from '@tanstack/react-query'
import { formatDate } from '@/lib/utils'
import { ArrowLeft, Building2, Calendar, DollarSign } from 'lucide-react'
import { useMemo } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'

type CarWithLineItems = {
  id?: string
  registrationNumber?: string
  lineItems: {
    id?: string
    identifier?: string
    description: string
    quantity: number
    unitPrice: number
    amountExclVat?: number
  }[]
}

const CarInvoiceLines = ({ car }: { car: CarWithLineItems }) => {
  const navigate = useNavigate()

  console.log('Rendering car invoice lines for', car)
  return (
    <div key={car.registrationNumber}>
      <div className="mb-4 pb-2 border-b">
        <div className="flex items-center justify-between">
          {car.registrationNumber && (
            <div>
              <h3 className="font-semibold text-lg">{car.registrationNumber}</h3>
            </div>
          )}
          {car.registrationNumber && (
            <Button variant="outline" size="sm" onClick={() => navigate(`/cars/${car.id}`)}>
              Visa bil
            </Button>
          )}
        </div>
      </div>

      <Table>
        <TableHeader>
          <TableRow>
            <TableHead className="">Art.nr</TableHead>
            <TableHead>Beskrivning</TableHead>
            <TableHead className="w-[80px] text-right">Antal</TableHead>
            <TableHead className="w-[120px] text-right">Enhetspris</TableHead>
            <TableHead className="w-[120px] text-right">Enhet totalpris</TableHead>
            <TableHead className="w-[60px] text-right"></TableHead>
            <TableHead className="w-[120px] text-right opacity-60">Rabattpris</TableHead>
            <TableHead className="w-[120px] text-right opacity-60">Total</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {car.lineItems.map((line, index) => (
            <TableRow key={`${car.registrationNumber}-${index}`}>
              <TableCell className="text-right">{line.identifier}</TableCell>
              <TableCell>{line.description}</TableCell>
              <TableCell className="text-right">{Number(line.quantity)}</TableCell>
              <TableCell className="text-right">
                {Number(line.unitPrice).toLocaleString('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                })}
              </TableCell>
              <TableCell className="text-right">
                {Number(line.unitPrice * line.quantity).toLocaleString('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                })}
              </TableCell>
              <TableCell className="text-right"></TableCell>
              <TableCell className="text-right opacity-60">
                {line.amountExclVat
                  ? Number(line.amountExclVat).toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK',
                    })
                  : '-'}
              </TableCell>
              <TableCell className="text-right opacity-60">
                {line.amountExclVat
                  ? Number(line.amountExclVat * line.quantity).toLocaleString('sv-SE', {
                      style: 'currency',
                      currency: 'SEK',
                    })
                  : '-'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </div>
  )
}

const InvoiceDetail = () => {
  const { id } = useParams<{ id: string }>()
  const [searchParams, setSearchParams] = useSearchParams()
  const carId = searchParams.get('carId')

  const navigate = useNavigate()

  const { data: invoice, isLoading } = useQuery({
    queryKey: ['invoice', id],
    queryFn: () => fetchInvoiceById(id!),
    enabled: !!id,
  })

  const { downloadUrl, loading: downloadUrlLoading } = useFileDownloadUrl(invoice?.downloadUrl)

  useDocumentTitle(invoice?.invoiceNumber ? `Faktura ${invoice.invoiceNumber}` : null)

  const { activeCar, otherCars, otherItems } = useMemo(() => {
    if (!invoice) return { activeCar: null, otherCars: [], otherItems: [] }

    const groupedCars = invoice.lineItems.reduce(
      (acc, lineItem) => {
        const existingCar = acc.find(c => c.registrationNumber === lineItem.registrationNumber)
        if (existingCar) {
          existingCar.lineItems.push(lineItem)
          return acc
        }

        acc.push({
          id: lineItem.id,
          registrationNumber: lineItem.registrationNumber,
          lineItems: [lineItem],
        })
        return acc
      },
      [] as { id: string; registrationNumber: string; lineItems: typeof invoice.lineItems }[],
    )

    if (carId) {
      const activeCar = groupedCars.find(c => c.id && c.id === carId) || null
      const groupedCarsWithoutActive = groupedCars.filter(c => c.id !== activeCar?.id)

      const otherCars = groupedCarsWithoutActive.filter(c => c.registrationNumber && c.registrationNumber !== carId)
      const otherItems = groupedCarsWithoutActive.filter(c => !c.registrationNumber)
      return { activeCar, otherCars, otherItems }
    }

    const otherCars = groupedCars.filter(c => c.registrationNumber)
    const otherItems = groupedCars.filter(c => !c.registrationNumber)

    return { activeCar: null, otherCars, otherItems }
  }, [invoice, carId])

  if (isLoading) {
    return (
      <AppLayout>
        <div className="space-y-6">
          <Skeleton className="h-10 w-64" />
          <Skeleton className="h-64" />
        </div>
      </AppLayout>
    )
  }

  if (!invoice) {
    return (
      <AppLayout>
        <div className="text-center py-12">
          <p className="text-muted-foreground">Faktura hittades inte</p>
        </div>
      </AppLayout>
    )
  }

  const totalUnitPrice = invoice.lineItems.reduce((sum, item) => sum + (item.unitPrice || 0), 0)
  return (
    <AppLayout>
      <div className="space-y-6">
        <div className="flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => navigate(-1)}>
            <ArrowLeft className="h-4 w-4 mr-2" />
            Tillbaka
          </Button>
        </div>

        <div>
          <h1 className="text-3xl font-bold">Faktura {invoice.invoiceNumber}</h1>
          <p className="text-muted-foreground mt-1">Fakturadetaljer</p>
        </div>

        <div className="grid gap-4 md:grid-cols-4">
          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Building2 className="h-4 w-4" />
                Verkstad
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold capitalize">{invoice.workshop || 'Ingen verkstad'}</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <Calendar className="h-4 w-4" />
                Fakturadatum
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold">
                {formatDate(invoice.dueDate, 'd MMMM yyyy')}
              </p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-sm flex items-center gap-2">
                <DollarSign className="h-4 w-4" />
                Total enhetspris
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-xl font-semibold text-accent-foreground">
                {Number(totalUnitPrice).toLocaleString('sv-SE', {
                  style: 'currency',
                  currency: 'SEK',
                })}
              </p>
            </CardContent>
          </Card>
        </div>

        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-2">
            <PdfViewer
              pdfUrl={downloadUrl}
              invoiceNumber={invoice.invoiceNumber}
              loading={isLoading || downloadUrlLoading}
            />
          </div>

          <div className="space-y-6 lg:col-span-3">
            {activeCar && (
              <Card>
                <CardHeader>
                  <CardTitle>Fakturarader för vald bil</CardTitle>
                </CardHeader>
                <CardContent>
                  <div key={activeCar.registrationNumber}>
                    <div className="mb-4 pb-2 border-b">
                      <div className="flex items-center justify-between">
                        <div>
                          <h3 className="font-semibold text-lg">{activeCar?.registrationNumber || 'Okänd bil'}</h3>
                        </div>
                        {activeCar && (
                          <Button variant="outline" size="sm" onClick={() => navigate(`/cars/${activeCar.id}`)}>
                            Visa bil
                          </Button>
                        )}
                      </div>
                    </div>

                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="">Art.nr</TableHead>
                          <TableHead>Beskrivning</TableHead>
                          <TableHead className="w-[80px] text-right">Antal</TableHead>
                          <TableHead className="w-[120px] text-right">Enhetspris</TableHead>
                          <TableHead className="w-[120px] text-right">Enhet totalpris</TableHead>
                          <TableHead className="w-[60px] text-right"></TableHead>
                          <TableHead className="w-[120px] text-right opacity-60">Rabattpris</TableHead>
                          <TableHead className="w-[120px] text-right opacity-60">Total</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {activeCar.lineItems.map((line, index) => (
                          <TableRow key={`${activeCar.registrationNumber}-${index}`}>
                            <TableCell className="text-right">{line.identifier}</TableCell>
                            <TableCell>{line.description}</TableCell>
                            <TableCell className="text-right">{Number(line.quantity)}</TableCell>
                            <TableCell className="text-right">
                              {Number(line.unitPrice).toLocaleString('sv-SE', {
                                style: 'currency',
                                currency: 'SEK',
                              })}
                            </TableCell>
                            <TableCell className="text-right">
                              {Number(line.unitPrice * line.quantity).toLocaleString('sv-SE', {
                                style: 'currency',
                                currency: 'SEK',
                              })}
                            </TableCell>
                            <TableCell className="text-right"></TableCell>
                            <TableCell className="text-right opacity-60">
                              {line.amountExclVat
                                ? Number(line.amountExclVat).toLocaleString('sv-SE', {
                                    style: 'currency',
                                    currency: 'SEK',
                                  })
                                : '-'}
                            </TableCell>
                            <TableCell className="text-right opacity-60">
                              {line.amountExclVat
                                ? Number(line.amountExclVat * line.quantity).toLocaleString('sv-SE', {
                                    style: 'currency',
                                    currency: 'SEK',
                                  })
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>
            )}

            {otherCars.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{carId ? 'Övriga bilar på fakturan' : 'Fakturarader per bil'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {otherCars.map(car => {
                    return <CarInvoiceLines key={car.id} car={car} />
                  })}

                  {invoice.lineItems.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Inga fakturarader hittades</p>
                  )}
                </CardContent>
              </Card>
            )}
            {otherItems.length > 0 && (
              <Card>
                <CardHeader>
                  <CardTitle>{'Övriga faktura rader'}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-8">
                  {otherItems.map(car => {
                    return <CarInvoiceLines key={car.id} car={car} />
                  })}

                  {invoice.lineItems.length === 0 && (
                    <p className="text-center text-muted-foreground py-8">Inga fakturarader hittades</p>
                  )}
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </div>
    </AppLayout>
  )
}

export default InvoiceDetail
